'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight, ChevronDown } from 'lucide-react';

const credentials = [
  { label: 'Industry-Informed Programmes' },
  { label: 'Executive Education' },
  { label: 'Built for African Professionals' },
  { label: 'Shaping the Future of Real Estate' },
];

export default function HeroSection() {
  const logoRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const logo = logoRef?.current;
    const content = contentRef?.current;
    if (!logo || !content) return;

    logo.style.opacity = '0';
    logo.style.transform = 'translateY(-12px) scale(0.97)';
    content.style.opacity = '0';
    content.style.transform = 'translateY(20px)';

    const t1 = setTimeout(() => {
      logo.style.transition = 'opacity 1.6s cubic-bezier(0.16,1,0.3,1), transform 1.6s cubic-bezier(0.16,1,0.3,1)';
      logo.style.opacity = '1';
      logo.style.transform = 'translateY(0) scale(1)';
    }, 200);

    const t2 = setTimeout(() => {
      content.style.transition = 'opacity 1.2s cubic-bezier(0.16,1,0.3,1), transform 1.2s cubic-bezier(0.16,1,0.3,1)';
      content.style.opacity = '1';
      content.style.transform = 'translateY(0)';
    }, 900);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <section
      suppressHydrationWarning
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Video background */}
      <video
        suppressHydrationWarning
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        src="https://reusa.education/wp-content/uploads/2024/01/REUSA-Hero-Video.mp4"
        autoPlay
        muted
        loop
        playsInline
        onCanPlay={() => setVideoLoaded(true)}
        aria-hidden="true"
      />
      {/* Fallback background */}
      <div
        suppressHydrationWarning
        className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1920&q=85')` }}
      />
      {/* Deep green overlay */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, rgba(20,30,24,0.94) 0%, rgba(26,43,34,0.88) 50%, rgba(14,22,17,0.95) 100%)' }} />
      {/* Vignette */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 35%, rgba(8,16,11,0.6) 100%)' }} />
      {/* Gold top line */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #c9a84c 30%, #e8c96a 50%, #c9a84c 70%, transparent 100%)' }} />
      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 pt-32 pb-28 w-full max-w-4xl mx-auto">

        {/* Large centered REUSA crest — dominant visual */}
        <div ref={logoRef} className="mb-14 flex flex-col items-center">
          <div className="relative drop-shadow-2xl w-[280px] h-[280px] sm:w-[380px] sm:h-[380px] md:w-[480px] md:h-[480px] lg:w-[620px] lg:h-[620px]">
            <Image
              src="/assets/images/REUSA_PNG_LOGO_2_-1779294693104.png"
              alt="REUSA Official Crest — Real Estate University of South Africa"
              fill
              className="object-contain"
              priority
              sizes="(max-width: 640px) 300px, (max-width: 1024px) 460px, 620px"
            />
          </div>
        </div>

        {/* CTA buttons + accreditation */}
        <div ref={contentRef} className="flex flex-col items-center w-full">
          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16 justify-center flex-wrap">
            <Link
              href="/apply"
              className="btn-gold text-base px-9 py-4 rounded-xl font-semibold tracking-wide"
            >
              Apply Now
              <ArrowRight size={17} />
            </Link>
            <Link
              href="/course-marketplace"
              className="btn-outline-white text-base px-9 py-4 rounded-xl font-semibold tracking-wide"
            >
              Explore Programmes
            </Link>
          </div>

          {/* Luxury credibility bar */}
          <div className="w-full flex justify-center">
            {/* Desktop: single row with gold dot dividers */}
            <div className="hidden sm:flex items-center justify-center gap-0">
              {credentials?.map((item, i) => (
                <React.Fragment key={item?.label}>
                  <span
                    className="text-white/60 uppercase font-sans font-medium"
                    style={{
                      fontSize: '0.65rem',
                      letterSpacing: '0.22em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item?.label}
                  </span>
                  {i < credentials?.length - 1 && (
                    <span
                      className="mx-5 flex-shrink-0 rounded-full"
                      style={{
                        width: '4px',
                        height: '4px',
                        background: 'rgba(201,168,76,0.75)',
                        display: 'inline-block',
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Mobile: 2x2 grid */}
            <div className="sm:hidden grid grid-cols-2 gap-x-6 gap-y-4 w-full max-w-xs mx-auto">
              {credentials?.map((item) => (
                <div key={item?.label} className="flex items-center justify-center gap-2">
                  <span
                    className="flex-shrink-0 rounded-full"
                    style={{ width: '4px', height: '4px', background: 'rgba(201,168,76,0.75)' }}
                  />
                  <span
                    className="text-white/60 uppercase font-sans font-medium text-center"
                    style={{ fontSize: '0.6rem', letterSpacing: '0.18em', lineHeight: '1.4' }}
                  >
                    {item?.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/25 animate-bounce">
        <ChevronDown size={18} />
      </div>
    </section>
  );
}